/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Julia Ignatieva
 * @version 1.15.0
 * @since 1.13.0
 */

import { useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { icons } from '../../../../../assets';
import { appHistoryPush } from '../../../../../modules/app/appHistory';
import { ELogBusinessEventName, ELogBusinessEventType } from '../../../../../modules/logging/loggingConstants';
import { EOnboardingComponentsId } from '../../../../../modules/onboarding/types/onboardingTypes';
import { AccessComponentKeyEnum } from '../../../../../modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '../../../../../modules/role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '../../../../../modules/wizard/wizardActions';
import { EWizardName } from '../../../../../modules/wizard/wizardConstants';
import { ExternalRoutePath, openExternalRoute } from '../../../../../routes/constants/externalRoutes';
import { IRouteParams, RoutePath, SUBSCRIBER_ID } from '../../../../../routes/constants/routesConstants';
import { stringFormat } from '../../../../../utils/stringFormat';
import { TableFooterNameType } from '../../../../Table/types/tableTypes';
import {
    IUseTableFooterButtons,
    TTableButtonsGetTranslation,
    TTableButtonsLogBusinessEvent,
    TUseTableFooterButtonsOnboarding,
    TUseTableFooterButtonsUrl,
} from '../useTableFooterButtons';

const useTableFooterButtonManageBlock = (
    getTranslation: TTableButtonsGetTranslation,
    logBusinessEvent: TTableButtonsLogBusinessEvent,
    tableFooterName: TableFooterNameType,
): IUseTableFooterButtons => {
    const dispatch = useDispatch();
    const { clientId } = useParams<IRouteParams>();

    const isWizardChangeLockEnabled =
        useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.PageWizardChangeLock));

    return {
        className: 'table-footer__link link-block',
        dataName: 'manageBlock',
        icon: icons.blockIcon,
        id: AccessComponentKeyEnum.FooterButtonLock,
        ...mapTableFooterNameToOnboarding[tableFooterName],
        ...getTranslation(AccessComponentKeyEnum.FooterButtonLock),

        onClick: useCallback(() => {
            dispatch(wizardStorageClearStepsAction({ wizardName: EWizardName.CHANGE_LOCK }));
            logBusinessEvent(ELogBusinessEventName.USER_BLOCK_MASTER_TO_STEP_1, ELogBusinessEventType.common);
            const urlConfig = mapTableFooterNameToUrl[tableFooterName];
            if (!urlConfig) return;

            isWizardChangeLockEnabled
                ? appHistoryPush(urlConfig.url.replace(SUBSCRIBER_ID, clientId))
                : openExternalRoute(stringFormat(urlConfig.external, clientId));
        }, [clientId, dispatch, isWizardChangeLockEnabled, logBusinessEvent, tableFooterName]),
    };
};

const mapTableFooterNameToUrl: Partial<TUseTableFooterButtonsUrl> = {
    [TableFooterNameType.subscriberId]: {
        url: RoutePath.wizardChangeLockFromSubscriberId,
        external: ExternalRoutePath.mobileLockId,
    },
    [TableFooterNameType.subscriberMobile]: {
        url: RoutePath.wizardChangeLockFromSubscribers,
        external: ExternalRoutePath.mobileLock,
    },
};

const mapTableFooterNameToOnboarding: TUseTableFooterButtonsOnboarding = {
    [TableFooterNameType.subscriberMobile]: {
        onboarding: EOnboardingComponentsId.subscriberTableFooterButtonManageBlock,
    },
};

export default useTableFooterButtonManageBlock;
